import { useEffect, useRef, useState } from 'react';
import {
  Button,
  Edit,
  ErrorState as RetryableErrorState,
} from '@react-apps/design-system';
import {
  sanitizeQuestionPreview,
  tokensToEditorHtml,
} from '@react-apps/content-editor';
import type {
  ContentItem,
  OfficialCommentWorkspace,
  QuestionBody,
} from '../../domain/entities/content.entity';
import { useUpdateContentItemBody } from '../hooks/use-content-items';
import {
  useOfficialCommentWorkspace,
  usePublishOfficialComment,
  useRefetchOfficialCommentWorkspace,
  useUnpublishOfficialComment,
} from '../hooks/use-official-comment';
import type { useContentEditLock } from '../hooks/use-content-edit-lock';
import { readQuestionBody } from '../exam-body';
import { isAnnulled } from '../answer-key';
import {
  ContentStatusBadge,
  getApiErrorMessage,
  notifyDone,
  notifyFailed,
  SectionCard,
} from '../display';
import { IconButton } from '@/shared/components/icon-button';
import {
  BodyForm,
  type BodyFormBuildRef,
  type BodyFormResult,
} from './BodyForm';
import { ConfirmModal } from './ConfirmModal';
import { EditLockNotice } from './EditLockNotice';
import {
  OfficialCommentField,
  type OfficialCommentRef,
} from './OfficialCommentField';
import { QuestionBodyView } from './QuestionBodyView';
import {
  QuestionClassificationField,
  type ClassificationRef,
} from './QuestionClassificationField';
import { QuestionClassificationChips } from './QuestionClassificationChips';
import { QuestionFlagBadges } from './QuestionFlagBadges';

/** As frases do card, uma vez só: o teste as procura pelo texto. */
export const QUESTION_CARD_MESSAGES = {
  unpublished: 'Comentário despublicado.',
  published: 'Comentário publicado.',
  draftWithReports:
    'Comentário despublicado: os reports abertos voltam a aparecer no pipefy quando ele for publicado.',
  noComment:
    'Esta questão ainda não tem comentário oficial. Use o lápis para escrever um.',
  loadFailed: 'Não foi possível carregar a questão e o comentário oficial.',
  nothingChanged: 'Questão: nada mudou, nada foi salvo',
  commentNothingChanged: 'Comentário: nada mudou, nada foi salvo',
  commentSaved: 'Comentário salvo',
} as const;

type Lock = ReturnType<typeof useContentEditLock>;
type Section = 'question' | 'comment';

/**
 * O form da ficha não soma o sujo dos campos: o `save()` pergunta a cada um
 * no clique, e não há guarda de troca de questão para avisar.
 */
const ignoreDirty = () => undefined;

/**
 * A questão na ficha dela (`/content/items/:contentId`), em duas seções: a
 * questão (enunciado, alternativas, marcações e assunto) e o comentário
 * oficial, cada uma com o seu lápis e o seu "Salvar". É o editor da questão
 * desde que a prova deixou de editá-la (decisão 11 do épico content-admin): o
 * card da prova só lê e leva até aqui.
 *
 * A leitura vem do workspace do comentário oficial (`GET
 * questions/:questionId/official-comment`), que traz a questão e o comentário
 * numa chamada. O form edita o item **cru** (`item`, com os tokens de imagem),
 * pelo mesmo motivo do antigo form do card da prova.
 *
 * **Um lock só, o da questão** (`lock`, da página): o `PUT` do comentário é
 * conferido pelo mesmo `questionId`. As duas seções podem estar abertas
 * juntas: o primeiro lápis pede o lock, o segundo só abre a seção, porque o
 * lock já é desta pessoa. Como o `finish` solta o lock inteiro, ele só é
 * chamado quando **a última** seção aberta fecha.
 */
export function QuestionCard({
  item,
  imageUrlsByFileId,
  lock,
}: {
  item: ContentItem;
  imageUrlsByFileId: Record<string, string>;
  lock: Lock;
}) {
  const workspace = useOfficialCommentWorkspace(item.id);
  // As seções abertas, que dividem o lock. Uma seção só entra depois que o
  // lock volta `held`: marcada no clique, um segundo clique (no outro lápis,
  // no mesmo tick) abriria a seção errada com o lock da primeira.
  const [open, setOpenState] = useState<Section[]>([]);
  // O mesmo, lido na hora: o "Salvar" fecha a seção depois de um `await`, e
  // um `open` capturado antes reabriria a seção que fechou nesse meio tempo.
  const openNow = useRef<Section[]>([]);
  const setOpen = (next: Section[]) => {
    openNow.current = next;
    setOpenState(next);
  };
  // A seção do último lápis clicado: o "Tentar de novo" do aviso abre essa.
  const requested = useRef<Section | null>(null);
  // O pedido no ar, gravado no clique: o `acquiring` só desabilita os lápis
  // no próximo render.
  const pending = useRef(false);
  // No `lost` os forms continuam montados, para a pessoa copiar o que escreveu.
  const holding = lock.editing || lock.lost;
  const isOpen = (section: Section) => holding && open.includes(section);
  const noticeId = `edit-lock-${item.id}`;
  const rawBody = readQuestionBody(item.body);

  const startSection = async (section: Section) => {
    // O lock já é desta pessoa (a outra seção está aberta): só abre.
    if (lock.editing && openNow.current.length > 0) {
      if (!openNow.current.includes(section)) {
        setOpen([...openNow.current, section]);
      }
      return;
    }
    if (pending.current) return;
    pending.current = true;
    requested.current = section;
    try {
      const state = await lock.start();
      if (state?.status === 'held') setOpen([section]);
    } finally {
      pending.current = false;
    }
  };

  /** Fecha uma seção; o lock só solta quando não sobra nenhuma aberta. */
  const finishSection = (section: Section) => {
    const rest = openNow.current.filter((other) => other !== section);
    setOpen(rest);
    if (rest.length === 0) lock.finish();
  };

  /** "Sair da edição" do aviso: fecha tudo e solta o lock. */
  const finishAll = () => {
    setOpen([]);
    lock.finish();
  };

  // Concluir e cancelar desmontam o form, e o botão que tinha o foco some com
  // ele: o foco volta ao lápis da seção que fechou, e não ao topo da página.
  const pencilRefs = {
    question: useRef<HTMLSpanElement>(null),
    comment: useRef<HTMLSpanElement>(null),
  };
  const wasOpen = useRef<Section[]>(open);
  useEffect(() => {
    const closed = wasOpen.current.find((section) => !open.includes(section));
    if (closed) pencilRefs[closed].current?.querySelector('button')?.focus();
    wasOpen.current = open;
  });

  // O aviso do lock é um só, acima das seções: `locked` e erro do pedido valem
  // para os dois lápis, e o `lost` vale para as duas seções abertas.
  const notice =
    lock.state?.status === 'locked' ||
    lock.state?.status === 'error' ||
    lock.state?.status === 'lost'
      ? lock.state
      : undefined;

  /** O lápis de uma seção: some com ela aberta. */
  const pencil = (section: Section, label: string) => {
    if (isOpen(section)) return undefined;
    return (
      <span ref={pencilRefs[section]}>
        <IconButton
          label={label}
          icon={<Edit size="sm" />}
          onClick={() => void startSection(section)}
          // Enquanto o `POST` do lock não volta, os dois lápis ficam no lugar,
          // ocupados e focáveis (`busy`): um segundo clique não manda um
          // segundo pedido, e o foco não cai no `body`. No `lost` não se abre
          // mais nada: o lock é de outra pessoa.
          disabled={!!item.deletedAt || !workspace.data || lock.lost}
          busy={lock.acquiring}
          blockedBy={notice?.status === 'locked' ? noticeId : undefined}
        />
      </span>
    );
  };

  const lockNotice = (
    <EditLockNotice
      id={noticeId}
      state={notice}
      retrying={lock.acquiring}
      onRetry={() => void startSection(requested.current ?? 'question')}
      onExit={finishAll}
    />
  );

  if (!workspace.data) {
    // As duas seções aparecem mesmo sem dado: quem abre a ficha vê onde o
    // comentário vai estar. Sem lápis em nenhuma enquanto não há o que editar.
    const body = workspace.isLoading ? (
      <p
        role="status"
        className="text-(--color-text-secondary) min-h-16 text-sm"
      >
        Carregando…
      </p>
    ) : (
      // A ficha é o único editor da questão: sem "Tentar de novo", a saída
      // seria o F5.
      <RetryableErrorState
        message={getApiErrorMessage(
          workspace.error,
          QUESTION_CARD_MESSAGES.loadFailed,
        )}
        onRetry={() => void workspace.refetch()}
      />
    );
    // A leitura é uma só: o estado sai uma vez, na seção da questão. A do
    // comentário só guarda o lugar, sem um segundo anúncio nem um segundo
    // "Tentar de novo" para o mesmo pedido.
    return (
      <>
        <SectionCard title="Questão" emphasis>
          {body}
        </SectionCard>
        <SectionCard title="Comentário oficial" emphasis>
          <div aria-hidden="true" className="min-h-16" />
        </SectionCard>
      </>
    );
  }

  const data = workspace.data;
  const urls = { ...imageUrlsByFileId, ...data.imageUrlsByFileId };
  const questionOpen = isOpen('question');
  const commentOpen = isOpen('comment');

  return (
    <>
      {lockNotice}

      <SectionCard
        title="Questão"
        emphasis
        action={pencil('question', 'Editar a questão')}
      >
        <div className="flex flex-wrap items-center gap-3">
          <QuestionFlagBadges body={rawBody} />
          <span className="text-(--color-text-secondary) text-xs">
            {data.question.format === 'ESSAY'
              ? 'Dissertativa'
              : 'Múltipla escolha'}
          </span>
          <QuestionClassificationChips
            classifications={data.question.subjects}
          />
        </div>

        {questionOpen ? (
          <QuestionEditForm
            item={item}
            workspace={data}
            imageUrlsByFileId={urls}
            lost={lock.lost}
            onDone={() => finishSection('question')}
          />
        ) : (
          <QuestionBodyView body={readableBody(data.question, urls)} />
        )}
      </SectionCard>

      <SectionCard
        title="Comentário oficial"
        emphasis
        action={pencil('comment', 'Editar o comentário oficial')}
      >
        {commentOpen ? (
          <OfficialCommentEditForm
            questionId={item.id}
            workspace={data}
            imageUrlsByFileId={urls}
            lost={lock.lost}
            onDone={() => finishSection('comment')}
          />
        ) : (
          <OfficialCommentPanel workspace={data} />
        )}
      </SectionCard>
    </>
  );
}

/**
 * O `QuestionBodyView` espera o HTML com `<img>` no lugar do token, e o
 * workspace traz os tokens: cada texto é resolvido pelo mapa de imagens.
 */
function readableBody(
  question: OfficialCommentWorkspace['question'],
  urls: Record<string, string>,
): QuestionBody {
  const html = (text: string) => tokensToEditorHtml(text, urls);
  return question.format === 'MULTIPLE_CHOICE'
    ? {
        format: 'MULTIPLE_CHOICE',
        stem: html(question.stem),
        alternatives: question.alternatives.map((alt) => ({
          id: alt.id,
          text: html(alt.text),
        })),
        correctAlternativeId: question.correctAlternativeId ?? undefined,
        annulled: question.annulled,
      }
    : {
        format: 'ESSAY',
        stem: html(question.stem),
        expectedAnswer:
          question.expectedAnswer === null
            ? undefined
            : html(question.expectedAnswer),
      };
}

type Confirm = 'unpublish' | 'publish' | null;

/**
 * O comentário em leitura, com o que se faz com ele fora da edição:
 * despublicar e publicar de novo, sempre com confirmação (decisão 10 do
 * README da Q/). Editar e criar são pelo lápis da própria seção.
 */
function OfficialCommentPanel({
  workspace,
}: {
  workspace: OfficialCommentWorkspace;
}) {
  const { question, comment } = workspace;
  const unpublish = useUnpublishOfficialComment(question.id);
  const publish = usePublishOfficialComment(question.id);
  const [confirm, setConfirm] = useState<Confirm>(null);
  // Anunciado em `aria-live`: quem usa leitor de tela não vê o badge mudar.
  const [announcement, setAnnouncement] = useState('');
  const actionsRef = useRef<HTMLDivElement>(null);
  const pending = confirm === 'unpublish' ? unpublish : publish;

  const runConfirmed = async () => {
    if (!comment || !confirm) return;
    try {
      await pending.mutateAsync(comment.id);
    } catch {
      // A mensagem sai no próprio `ConfirmModal`, pelo `error` da mutação.
      return;
    }
    setAnnouncement(
      confirm === 'unpublish'
        ? QUESTION_CARD_MESSAGES.unpublished
        : QUESTION_CARD_MESSAGES.published,
    );
    setConfirm(null);
    // O botão confirmado some (Despublicar vira Publicar): o foco vai ao que
    // ficou no lugar, e não cai no `body` quando o modal devolve o foco.
    requestAnimationFrame(() =>
      actionsRef.current?.querySelector('button')?.focus(),
    );
  };

  return (
    <div className="space-y-4">
      <p aria-live="polite" className="sr-only">
        {announcement}
      </p>

      {comment === null ? (
        <p className="text-(--color-text-secondary) text-sm">
          {QUESTION_CARD_MESSAGES.noComment}
        </p>
      ) : (
        <div className="space-y-4">
          <div className="flex flex-wrap items-center gap-2">
            <ContentStatusBadge status={comment.status} />
            <span className="text-(--color-text-secondary) text-xs">
              {comment.authorName ? `por ${comment.authorName}` : 'sem autor'}
            </span>
          </div>

          {comment.status === 'DRAFT' && workspace.openReports > 0 && (
            <p
              role="status"
              className="rounded-lg border border-amber-500/40 bg-amber-500/10 p-3 text-sm text-amber-800 dark:text-amber-300"
            >
              {QUESTION_CARD_MESSAGES.draftWithReports}
            </p>
          )}

          <div
            className="rich-text rich-text-read text-(--color-text-primary) text-sm"
            // Os tokens viram `<img>` pelo mapa, e o HTML passa pela mesma
            // sanitização da questão (allowlist + `img`).
            dangerouslySetInnerHTML={{
              __html: sanitizeQuestionPreview(
                tokensToEditorHtml(comment.text, workspace.imageUrlsByFileId),
              ),
            }}
          />

          <div ref={actionsRef} className="flex flex-wrap gap-2">
            {comment.status === 'PUBLISHED' && (
              <Button
                variant="secondary"
                onClick={() => setConfirm('unpublish')}
              >
                Despublicar comentário
              </Button>
            )}
            {comment.status === 'DRAFT' && (
              <Button variant="secondary" onClick={() => setConfirm('publish')}>
                Publicar comentário
              </Button>
            )}
          </div>
        </div>
      )}

      <ConfirmModal
        open={confirm !== null}
        onClose={() => {
          setConfirm(null);
          pending.reset();
        }}
        onConfirm={() => void runConfirmed()}
        title={
          confirm === 'unpublish'
            ? 'Despublicar o comentário?'
            : 'Publicar o comentário?'
        }
        description={
          confirm === 'unpublish'
            ? 'O aluno deixa de ver o comentário, e os reports abertos saem do pipefy até ele ser publicado de novo. Nada é apagado.'
            : 'O comentário volta a aparecer para o aluno, e os reports abertos voltam ao pipefy.'
        }
        confirmLabel={confirm === 'unpublish' ? 'Despublicar' : 'Publicar'}
        danger={confirm === 'unpublish'}
        loading={pending.isPending}
        error={pending.error}
      />
    </div>
  );
}

/**
 * Modo edição da questão: enunciado e alternativas e a classificação num form
 * só, com um "Salvar". Cada parte vai na sua requisição, e só sai a que mudou:
 *
 * | Parte | Requisição |
 * | --- | --- |
 * | Enunciado e alternativas | `PATCH items/:contentId` |
 * | Classificação | `PUT items/:contentId/subjects` |
 *
 * O comentário tem a seção e o form dele (`OfficialCommentEditForm`).
 *
 * Um `PATCH` idêntico avançaria a `version` e gravaria um `ContentBodyUpdated`
 * que não descreve mudança nenhuma.
 */
function QuestionEditForm({
  item,
  workspace,
  imageUrlsByFileId,
  lost,
  onDone,
}: {
  item: ContentItem;
  workspace: OfficialCommentWorkspace;
  imageUrlsByFileId: Record<string, string>;
  /** Perdeu o lock: o texto fica, mas o "Salvar" não. */
  lost: boolean;
  /** Concluir e cancelar: solta o lock e volta à leitura. */
  onDone: () => void;
}) {
  const update = useUpdateContentItemBody(item.id);
  const refetchWorkspace = useRefetchOfficialCommentWorkspace(item.id);
  const [dirtyBody, setDirtyBody] = useState(false);
  const buildRef: BodyFormBuildRef = useRef(null);
  const classificationRef: ClassificationRef = useRef(null);
  // O body do último `PATCH` que passou. Uma falha depois dele (o `PUT` da
  // classificação) deixa o form aberto, e o `BodyForm` segue comparando com o
  // body de quando abriu: o "Salvar" seguinte regravaria o mesmo body, e um
  // enunciado desfeito de volta ao original se diria "sem alteração" com o
  // servidor guardando o intermediário. Depois do primeiro `PATCH`, quem
  // decide é a comparação com este.
  const savedBody = useRef<string | null>(null);
  // A classificação de quando o lápis foi clicado. O workspace relê no foco da
  // janela, e o campo compara com o que recebe: um assunto trocado por outra
  // aba mudaria o "alterado?" no meio da edição.
  const [subjects] = useState(() => workspace.question.subjects);

  /** `false` quando a falha deixa o form aberto, com o erro à vista. */
  const save = async (built: BodyFormResult): Promise<boolean> => {
    // O que de fato foi gravado, na ordem em que foi. Vira o texto do aviso:
    // "Questão salva" não diz se a classificação entrou junto.
    const saved: string[] = [];
    try {
      const bodyKey = JSON.stringify(built.body);
      const bodyChanged =
        savedBody.current === null ? dirtyBody : bodyKey !== savedBody.current;
      if (bodyChanged) {
        await update.mutateAsync({ body: built.body, images: built.images });
        savedBody.current = bodyKey;
        saved.push('enunciado');
      }
      if (classificationRef.current?.isDirty()) {
        await classificationRef.current.save();
        saved.push('classificação');
      }
      if (saved.length === 0) {
        notifyDone(QUESTION_CARD_MESSAGES.nothingChanged);
        return true;
      }
      // A leitura sai com o que acabou de ser gravado, e não com a foto que
      // estava em cache quando o lápis foi clicado.
      await refetchWorkspace();
      notifyDone(`Questão salva — ${saved.join(', ')}`);
      return true;
    } catch (err) {
      // O inline (erro do campo) diz ONDE falhou; o toast diz QUE falhou.
      notifyFailed(err, 'A questão não foi salva');
      return false;
    }
  };

  return (
    <BodyForm
      type="QUESTION"
      body={item.body}
      pending={update.isPending || lost}
      error={update.error}
      imageUrlsByFileId={imageUrlsByFileId}
      annulled={isAnnulled(readQuestionBody(item.body))}
      onDirtyChange={setDirtyBody}
      buildRef={buildRef}
      onSubmit={(result) => {
        void save(result).then((ok) => {
          if (ok) onDone();
        });
      }}
      onCancel={onDone}
      /* A classificação entra DENTRO do form, antes do Salvar — é o que
           deixa um botão só salvando o que está na seção. */
      extraSections={
        <div className="border-(--color-border) border-t pt-3">
          <QuestionClassificationField
            questionId={item.id}
            current={subjects}
            onDirtyChange={ignoreDirty}
            fieldRef={classificationRef}
          />
        </div>
      }
    />
  );
}

/**
 * Modo edição do comentário: o campo e o "Salvar" dele. Só sai o `PUT
 * questions/:questionId/official-comment`, e só se o texto mudou — o `PUT`
 * passa o crédito para quem salva, então abrir e salvar sem mexer não pode
 * mandá-lo.
 *
 * Sem comentário, o campo nasce vazio e salvar o cria (decisão 8 do épico
 * content-admin). A falha deixa o form aberto com o texto, e o erro sai no
 * próprio campo (`save.error`, já traduzido, inclusive o 409 do lock).
 */
function OfficialCommentEditForm({
  questionId,
  workspace,
  imageUrlsByFileId,
  lost,
  onDone,
}: {
  questionId: string;
  workspace: OfficialCommentWorkspace;
  imageUrlsByFileId: Record<string, string>;
  /** Perdeu o lock: o texto fica, mas o "Salvar" não. */
  lost: boolean;
  /** Concluir e cancelar: solta o lock e volta à leitura. */
  onDone: () => void;
}) {
  const refetchWorkspace = useRefetchOfficialCommentWorkspace(questionId);
  const commentRef: OfficialCommentRef = useRef(null);
  const [saving, setSaving] = useState(false);
  // Salvar no meio da geração gravaria o texto de antes do rascunho.
  const [generating, setGenerating] = useState(false);
  const { comment } = workspace;

  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!commentRef.current?.isDirty()) {
      notifyDone(QUESTION_CARD_MESSAGES.commentNothingChanged);
      onDone();
      return;
    }
    setSaving(true);
    try {
      await commentRef.current.save();
      // A leitura sai com o que acabou de ser gravado.
      await refetchWorkspace();
      notifyDone(QUESTION_CARD_MESSAGES.commentSaved);
      onDone();
    } catch (err) {
      notifyFailed(err, 'O comentário não foi salvo');
    } finally {
      setSaving(false);
    }
  };

  const blocked = saving || lost || generating;
  return (
    <form onSubmit={(event) => void save(event)} className="space-y-4">
      <OfficialCommentField
        questionId={questionId}
        comment={
          comment && {
            id: comment.id,
            status: comment.status,
            text: comment.text,
            author: comment.authorName,
          }
        }
        imageUrlsByFileId={imageUrlsByFileId}
        onDirtyChange={ignoreDirty}
        onGeneratingChange={setGenerating}
        harrisonDisabled={lost}
        fieldRef={commentRef}
      />
      <div className="flex flex-wrap items-center gap-2">
        <Button type="submit" disabled={blocked}>
          {generating ? 'Aguardando o Harrison…' : 'Salvar'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={onDone}
          disabled={saving}
        >
          Cancelar
        </Button>
      </div>
    </form>
  );
}
